"use client"

import { useEffect, useState } from "react"
import Link from "next/link"
import { useRouter } from "next/navigation"
import { ChevronLeft, Sparkles } from "lucide-react"
import { Button } from "~/components/ui/button"
import { Card, CardContent } from "~/components/ui/card"
import { Skeleton } from "~/components/ui/skeleton"
import { createClient } from "~/lib/supabase/client"
import { env } from "~/env"
import { PathCoinsWalletCard, type PathCoinsBalance as Balance } from "~/components/path-coins/PathCoinsWalletCard"
import { withImpersonation } from '~/lib/impersonation-client'
import { LoadError } from "~/components/ui/LoadError"

interface Transaction {
  id: string
  amount: number
  reason: string
  source_type: string
  source_id: string
  created_at: string
}

interface EarnRule {
  reason: string
  amount: number
  default_amount: number
}

// Human-readable labels for the PathCoinReason enum. Keep in sync with the
// values in apps/api/prisma/schema.prisma + path-coins.constants.ts.
const REASON_LABELS: Record<string, string> = {
  SIGNUP: "Welcome to Path Collective",
  PROFILE_COMPLETE: "Completed your profile",
  FIRST_BOOKING: "First paid booking",
  SUBSEQUENT_BOOKING: "Paid booking",
  CHECK_IN: "Checked into event",
  REVIEW_SUBMITTED: "Wrote a review",
  FACILITATOR_FOLLOWED: "Followed a facilitator",
  EVENT_SHARED: "Shared an event",
  REFEREE: "Joined via a friend",
  REFERRER: "Referred a friend who booked",
  FACILITATOR_COMMENT: "Commented on a facilitator profile",
}

function reasonLabel(reason: string): string {
  return REASON_LABELS[reason] ?? reason.replace(/_/g, " ").toLowerCase()
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  })
}

export default function PathCoinsLedgerPage() {
  const router = useRouter()
  const [balance, setBalance] = useState<Balance | null>(null)
  const [transactions, setTransactions] = useState<Transaction[]>([])
  const [earnRules, setEarnRules] = useState<EarnRule[]>([])
  const [monthEarned, setMonthEarned] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  // #1179 — bumped by Retry to re-run the load.
  const [reloadKey, setReloadKey] = useState(0)

  useEffect(() => {
    const load = async () => {
      setLoading(true)
      setError(null)
      try {
        const supabase = createClient()
        const { data: { session } } = await supabase.auth.getSession()
        if (!session?.access_token) {
          router.push("/login")
          return
        }

        const authHeader = { Authorization: `Bearer ${withImpersonation(session.access_token)}` }
        const [balanceRes, txRes, rulesRes] = await Promise.all([
          fetch(`${env.NEXT_PUBLIC_API_URL}/me/path-coins/balance`, { headers: authHeader }),
          fetch(`${env.NEXT_PUBLIC_API_URL}/me/path-coins/transactions?limit=50`, { headers: authHeader }),
          fetch(`${env.NEXT_PUBLIC_API_URL}/me/path-coins/earn-rules`),
        ])

        // Monthly contribution for the wallet progress bar — supplementary,
        // so it degrades silently like on the Home dashboard.
        fetch(`${env.NEXT_PUBLIC_API_URL}/me/analytics`, { headers: authHeader })
          .then((r) => (r.ok ? r.json() : null))
          .then((a) => setMonthEarned(a?.this_month?.pathcoins_earned ?? 0))
          .catch(() => {})

        if (!balanceRes.ok || !txRes.ok || !rulesRes.ok) {
          throw new Error("Failed to load your PathCoins")
        }
        setBalance(await balanceRes.json())
        const txBody = await txRes.json()
        setTransactions(txBody.items ?? [])
        setEarnRules(await rulesRes.json())
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to load your PathCoins")
      } finally {
        setLoading(false)
      }
    }
    void load()
  }, [router, reloadKey])

  return (
    <div className="min-h-screen bg-background">
      <div className="mx-auto max-w-3xl px-4 py-8">
        <div className="mb-6">
          <Button variant="ghost" size="sm" asChild>
            <Link href="/me">
              <ChevronLeft className="h-4 w-4 mr-1" />
              Back to account
            </Link>
          </Button>
        </div>

        <header className="mb-8 space-y-2">
          <h1 className="flex items-center gap-2 text-3xl font-semibold text-neutral-emphasis">
            <Sparkles className="h-7 w-7 text-primary" />
            PathCoins
          </h1>
          <p className="text-sm text-neutral">
            You earn PathCoins for engaging with Path Collective. Redemption is coming
            soon — for now, the counter only goes up.
          </p>
        </header>

        <div className="mb-8">
          {loading ? (
            <Skeleton className="h-56 w-full rounded-[20px]" />
          ) : (
            <PathCoinsWalletCard
              balance={balance}
              monthEarned={monthEarned}
              balanceTestId="pathcoins-page-balance"
            />
          )}
        </div>

        <section className="mb-8">
          <h2 className="mb-3 text-lg font-semibold text-neutral-emphasis">History</h2>
          {error ? (
            <LoadError message={error} onRetry={() => setReloadKey((k) => k + 1)} />
          ) : loading ? (
            <div className="space-y-2">
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
            </div>
          ) : transactions.length === 0 ? (
            <Card>
              <CardContent className="p-6 text-sm text-neutral">
                No earnings yet. Start by following a facilitator or booking an event.
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="p-0">
                <ul className="divide-y divide-border" data-testid="pathcoins-transactions">
                  {transactions.map((tx) => (
                    <li
                      key={tx.id}
                      className="flex items-center justify-between px-6 py-4"
                      data-testid="pathcoins-transaction"
                    >
                      <div>
                        <p className="text-sm font-medium text-neutral-emphasis">
                          {reasonLabel(tx.reason)}
                        </p>
                        <p className="text-xs text-neutral">{formatDate(tx.created_at)}</p>
                      </div>
                      <span className="text-base font-semibold text-primary">
                        +{tx.amount}
                      </span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}
        </section>

        <section>
          <h2 className="mb-3 text-lg font-semibold text-neutral-emphasis">How to earn</h2>
          {loading ? (
            <Skeleton className="h-40 w-full" />
          ) : (
            <Card>
              <CardContent className="p-0">
                <ul className="divide-y divide-border">
                  {earnRules
                    .filter((r) => r.amount > 0)
                    .map((rule) => (
                      <li
                        key={rule.reason}
                        className="flex items-center justify-between px-6 py-3"
                      >
                        <span className="text-sm text-neutral-emphasis">
                          {reasonLabel(rule.reason)}
                        </span>
                        <span className="text-sm font-semibold text-primary">
                          +{rule.amount}
                        </span>
                      </li>
                    ))}
                </ul>
              </CardContent>
            </Card>
          )}
        </section>
      </div>
    </div>
  )
}
